import React, { useState, useEffect, memo } from 'react'
import api from 'helpers/api'
import CommentUser from 'components/comments/commentUserDetails'
import { ICommentObject, ActionMenuType } from 'types'
import Button from 'components/button'
import { useApplicationStore } from 'hooks/application'
import CommentActionsMenu from '../commentActionsMenu'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import CommentEditor from 'components/comments/commentEditor'
import CommentBody from 'components/comments/commentBody'
import { MentionMember, editorTextToMarkdown } from 'helpers/mentions'

interface ICommentActionsButtonsProps {
  projectId: string
  action: ActionMenuType | null
  comment: ICommentObject
  commentThreadId: string
  commentText: string
  members: MentionMember[]
  spreadsheetData?: ISpreadsheetData
  onYesButton: (comment: ICommentObject) => void
  onCancelButton: () => void
}

const CommentActionsButtons: React.FC<ICommentActionsButtonsProps> = ({
  projectId,
  action,
  comment,
  commentThreadId,
  commentText,
  members,
  spreadsheetData,
  onYesButton,
  onCancelButton
}) => {
  const [disabled, setDisabled] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const { displayErrorMessage } = useApplicationStore()

  const handleOnSelectedActionButton = () => {
    setDisabled(true)
    setIsProcessing(true)
    let additionalContext = {}
    if (spreadsheetData) {
      additionalContext = {
        processPublicId: spreadsheetData.processId,
        processSectionPublicId: spreadsheetData.processSectionId
      }
    }
    if (action === 'edit') {
      const data = {
        commentText: editorTextToMarkdown(commentText, members),
        context: { projectId, ...additionalContext }
      }
      api
        .updateComment(commentThreadId, comment.publicId, data)
        .then((response) => {
          onYesButton(response.data)
          setIsProcessing(false)
        })
        .catch((error) => displayErrorMessage(error))
    } else {
      const context = { context: { projectId, ...additionalContext } }
      api
        .deleteComment(context, commentThreadId, comment.publicId)
        .then((response) => {
          onYesButton(response.data)
          setIsProcessing(false)
        })
        .catch((error) => displayErrorMessage(error))
    }
  }

  return (
    <div className="block m-[5px]">
      {action != 'edit' && (
        <div>
          <span className="h-5">Are you sure you want to</span> <span className="bold">{action}</span>{' '}
          <span>this comment?</span>
        </div>
      )}
      <div className="inline-flex">
        <Button
          className="mr-[5px] min-w-20"
          onClick={() => handleOnSelectedActionButton()}
          disabled={disabled || !commentText}
          isLoading={isProcessing}
        >
          {action === 'edit' ? 'Update' : 'Yes'}
        </Button>
        <Button className="mr-[5px] w-20" internalType="danger" onClick={() => onCancelButton()}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

interface ICommentComponentProps {
  projectId: string
  commentThreadId: string
  isResolvedCommentThread: boolean
  initialComment: ICommentObject
  index: number
  members: MentionMember[]
  spreadsheetData?: ISpreadsheetData
  onCommentChange: (index: number, comment: ICommentObject) => void
}

// Editable representation of a stored comment, used to populate the editor: mention
// tokens become @Name. Legacy plain text passes through unchanged.
const toEditable = (comment: ICommentObject): string => comment.commentText

const ReplyCommentComponent: React.FC<ICommentComponentProps> = ({
  projectId,
  commentThreadId,
  isResolvedCommentThread,
  initialComment,
  index,
  members,
  spreadsheetData,
  onCommentChange
}) => {
  const [comment, setComment] = useState(initialComment)
  const [commentText, setCommentText] = useState(toEditable(initialComment))

  const [readOnly, setReadOnly] = useState(true)
  const [selectedAction, setSelectedAction] = useState<ActionMenuType | null>(null)

  useEffect(() => {
    if (isResolvedCommentThread) {
      setCommentText(toEditable(comment))
      setReadOnly(true)
      setSelectedAction(null)
    }
  }, [isResolvedCommentThread])

  const onSelecteActionMenu = (action: ActionMenuType) => {
    setSelectedAction(action)
    switch (action) {
      case 'edit':
        // Reset the editor from the latest saved body so edit mode never opens
        // with stale text (or unresolved tokens) after a previous successful edit.
        setCommentText(toEditable(comment))
        setReadOnly(!readOnly)
        break
      case 'delete':
        setCommentText(toEditable(comment))
        setReadOnly(true)
        break
    }
  }

  const handleYesButton = (comment: ICommentObject) => {
    setComment(comment)
    setReadOnly(true)
    setSelectedAction(null)
    onCommentChange(index, comment)
  }

  const handleCanelButton = () => {
    setCommentText(toEditable(comment))
    setReadOnly(true)
    setSelectedAction(null)
  }

  if (comment.deletedAt) {
    return null
  }

  return (
    <div className="flex flex-col w-full py-[5px] mx-0 px-[5px]">
      <div className="inline-flex justify-between w-full">
        <CommentUser comment={comment} />
        <div className="inline-flex">
          {comment.isOwner && !isResolvedCommentThread && (
            <CommentActionsMenu onSelectedActionMenu={(action: ActionMenuType) => onSelecteActionMenu(action)} />
          )}
        </div>
      </div>
      {readOnly ? (
        <div className="bg-surface select-text p-2.5 rounded-lg">
          <CommentBody markdown={comment.commentText} members={members} />
        </div>
      ) : (
        <CommentEditor
          id={`comment-${comment.publicId}`}
          value={commentText}
          onChange={setCommentText}
          members={members}
          focusOnMount
        />
      )}
      {selectedAction && !isResolvedCommentThread && (
        <CommentActionsButtons
          projectId={projectId}
          action={selectedAction}
          comment={comment}
          commentText={commentText}
          commentThreadId={commentThreadId}
          members={members}
          spreadsheetData={spreadsheetData}
          onYesButton={(updatedComment: ICommentObject) => handleYesButton(updatedComment)}
          onCancelButton={() => handleCanelButton()}
        />
      )}
    </div>
  )
}

function areEqualComment(prevComment: ICommentObject, nextComment: ICommentObject) {
  return (
    prevComment.deletedAt === nextComment.deletedAt &&
    prevComment.updatedAt === nextComment.updatedAt &&
    prevComment.commentText === nextComment.commentText &&
    prevComment.isOwner === prevComment.isOwner &&
    prevComment.user.firebaseUserId === prevComment.user.firebaseUserId
  )
}

function areEqual(prevProps: ICommentComponentProps, nextProps: ICommentComponentProps) {
  return (
    prevProps.commentThreadId === nextProps.commentThreadId &&
    prevProps.index === nextProps.index &&
    prevProps.initialComment === nextProps.initialComment &&
    prevProps.isResolvedCommentThread === nextProps.isResolvedCommentThread &&
    prevProps.members === nextProps.members &&
    areEqualComment(prevProps.initialComment, nextProps.initialComment)
  )
}

export default memo(ReplyCommentComponent, areEqual)
